"use client";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import { api } from "@/shared/lib/api";
import { PredraftChampionImage } from "@/features/predraft/ui/predraft-champion-image";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";

/** Один выбор в драфте: чемпион на позиции набора стороны (KAN-821). */
interface MatchDraftSelection {
  order: number;
  /** Капитан, который набирал сторону. */
  draftedByUserId: string | null;
  draftedByName: string | null;
  championId: string;
}

interface MatchDraftGame {
  gameNumber: number;
  bluePicks: MatchDraftSelection[];
  redPicks: MatchDraftSelection[];
  blueBans: MatchDraftSelection[];
  redBans: MatchDraftSelection[];
}

interface MatchDraftResponse {
  matchId: string;
  games: MatchDraftGame[];
}

interface MatchDraftResultProps {
  matchId: string;
  blueName?: string | null;
  redName?: string | null;
  className?: string;
}

function PickRow({ selection }: { selection: MatchDraftSelection }) {
  // Пустой championId — ход пропущен по таймауту. Это часть истории драфта,
  // поэтому строка остаётся, но без чемпиона.
  return (
    <div className="flex items-center gap-2">
      {selection.championId ? (
        <PredraftChampionImage championId={selection.championId} alt={selection.championId} size={28} lazy />
      ) : (
        <div className="flex size-7 items-center justify-center rounded border border-border text-muted-foreground text-xs">
          —
        </div>
      )}
      <div className="min-w-0">
        <div className="truncate text-sm text-foreground">{selection.championId || "—"}</div>
      </div>
    </div>
  );
}

/** Кто набирал сторону: драфтит один человек — капитан (KAN-818). */
function DrafterLine({ selections }: { selections: MatchDraftSelection[] }) {
  const name = selections.find((s) => s.draftedByName)?.draftedByName;
  if (!name) return null;
  return <div className="text-muted-foreground text-xs">Драфтил: {name}</div>;
}

function BanStrip({ selections }: { selections: MatchDraftSelection[] }) {
  if (selections.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {selections.map((s) => (
        <div
          key={`${s.order}-${s.championId}`}
          className="relative size-7 overflow-hidden rounded border border-border"
          title={s.championId || "—"}
        >
          {s.championId ? (
            <>
              <PredraftChampionImage championId={s.championId} alt={s.championId} size={28} lazy />
              <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-destructive text-xs font-bold">
                ✕
              </span>
            </>
          ) : (
            <span className="flex h-full w-full items-center justify-center text-muted-foreground text-xs">—</span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Драфт матча: что команды набрали в комнате пре-драфта (KAN-821).
 * До этого кана драфт жил только в Redis и исчезал через два часа.
 */
export function MatchDraftResult({ matchId, blueName, redName, className }: MatchDraftResultProps) {
  const tUi = useTranslations("Ui");
  const { data } = useQuery({
    queryKey: ["match", matchId, "draft"],
    queryFn: async () => {
      const res = await api.get<MatchDraftResponse>(`/matches/${matchId}/draft`);
      return res.data;
    },
    enabled: !!matchId,
    staleTime: 60_000,
  });

  const games = data?.games ?? [];
  if (games.length === 0) return null;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{tUi("matchDraftResult.draft")}</CardTitle>
        <CardDescription>{tUi("matchDraftResult.pikiIBanyIz")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {games.map((game) => (
          <div key={game.gameNumber} className="flex flex-col gap-3">
            {games.length > 1 && (
              <div className="text-muted-foreground text-xs font-medium uppercase tracking-widest">
                Игра {game.gameNumber}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <div className="text-primary text-sm font-semibold">{blueName ?? tUi("litMatchDraftResult.sinie")}</div>
                <DrafterLine selections={game.bluePicks} />
                {game.bluePicks.map((s) => (
                  <PickRow key={`b-${s.order}`} selection={s} />
                ))}
                <BanStrip selections={game.blueBans} />
              </div>
              <div className="flex flex-col gap-2">
                <div className="text-destructive text-sm font-semibold">{redName ?? tUi("litMatchDraftResult.krasnye")}</div>
                <DrafterLine selections={game.redPicks} />
                {game.redPicks.map((s) => (
                  <PickRow key={`r-${s.order}`} selection={s} />
                ))}
                <BanStrip selections={game.redBans} />
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
